import { PageComponent } from '@root/pages/Type';
import Skeleton from 'react-loading-skeleton';
import { Accordion, Card, useAccordionToggle } from 'react-bootstrap';
import AccordionContext from 'react-bootstrap/AccordionContext';
import { default as CaretDown } from '@static/img/icons/caret-down.svg';
import { default as CaretUp } from '@static/img/icons/caret-up.svg';
import { MethodRecord } from '../Types';
import { MacroMethodsProps } from './Methods';
import MethodsContainer from './MethodsContainer';
import styles from './Macro.module.scss';
import classNames from 'classnames';
import { useContext } from 'react';
import { Element, scroller } from 'react-scroll';

export type MethodsContainersProps = Omit<MacroMethodsProps, 'methods'> & {
    methods: MethodRecord[];
    isLoading: boolean;
};

function SmartAccordionToggle({ children, eventKey }) {
    const currentEventKey = useContext(AccordionContext);
    const isCurrentEventKey = currentEventKey === eventKey;
    return (
        <h5 className={classNames({ [styles.active]: isCurrentEventKey })}>
            <span>{children}</span>
            {isCurrentEventKey ? <CaretDown /> : <CaretUp />}
        </h5>
    );
}

const MethodsContrainersStandart: PageComponent<MethodsContainersProps> = (
    props,
) => {
    const { isLoading, methods } = props;
    return (
        <>
            {methods.map((method) => {
                return (
                    <div
                        key={method.title}
                        className={styles.methods_container}
                    >
                        <h3 className={styles.title_container}>
                            {isLoading ? <Skeleton /> : method.title}
                        </h3>
                        <MethodsContainer {...props} methods={method.items} />
                    </div>
                );
            })}
        </>
    );
};

type MethodsContrainersAccordionCardProps = Omit<
    MethodsContainersProps,
    'methods'
> & {
    method: MethodRecord;
    isLoading: boolean;
};

const MethodsContrainersAccordionCard: PageComponent<
    MethodsContrainersAccordionCardProps
> = ({ method, isLoading, ...props }) => {
    const decoratedOnClick = useAccordionToggle(method.title);
    return (
        <Element name={`payment-${method.title}`}>
            <Card className={styles.card}>
                <Card.Header
                    className={styles.card_header}
                    onClick={(event) => {
                        decoratedOnClick(event);
                        setTimeout(() => {
                            scroller.scrollTo(`payment-${method.title}`, {
                                duration: 1500,
                                delay: 0,
                                smooth: true,
                            });
                        }, 350);
                    }}
                >
                    <SmartAccordionToggle eventKey={method.title}>
                        {isLoading ? <Skeleton /> : method.title}
                    </SmartAccordionToggle>
                </Card.Header>
                <Accordion.Collapse
                    eventKey={method.title}
                    className={classNames(styles.collapse)}
                >
                    <Card.Body className={styles.card_body}>
                        <MethodsContainer
                            {...props}
                            isLoading={isLoading}
                            methods={method.items}
                        />
                    </Card.Body>
                </Accordion.Collapse>
            </Card>
        </Element>
    );
};

const MethodsContrainersAccordion: PageComponent<MethodsContainersProps> = (
    props,
) => {
    const { isLoading, methods } = props;
    return (
        <Accordion className={styles.accordion}>
            {methods.map((method) => (
                <MethodsContrainersAccordionCard
                    {...props}
                    key={method.title}
                    isLoading={isLoading}
                    method={method}
                />
            ))}
        </Accordion>
    );
};

const MethodsContrainers: PageComponent<MethodsContainersProps> = (props) => {
    const { methods } = props;
    return (
        <>
            <MethodsContrainersStandart
                {...props}
                methods={methods.filter((m) => m.isFavorite || m.isGeo)}
            />
            <MethodsContrainersAccordion
                {...props}
                methods={methods.filter((m) => !m.isFavorite && !m.isGeo)}
            />
        </>
    );
};

export default MethodsContrainers;
